import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  CartItemDTO,
  CustomerDTO,
  PaymentType,
  CartTotals,
  DailySummaryDTO,
  ProductSearchResult,
} from '../../types/billing';
import {
  createBillAPI,
  searchProductByCodeAPI,
  searchCustomersByPhoneAPI,
  fetchCustomersAPI,
  createCustomerAPI,
  fetchDailySummaryAPI,
} from './billingAPI';
import { InventoryItemDTO } from '../../types/inventory';

// Async thunks
export const searchProductByCode = createAsyncThunk<
  any, // Using any to handle the raw API response structure
  string,
  { rejectValue: string }
>('billing/searchProductByCode', async (code, { rejectWithValue }) => {
  try {
    const result = await searchProductByCodeAPI(code);
    console.log('billingSlice - searchProductByCode API result:', result);
    
    // Check if product was actually found
    // API returns: { status: true, data: { status: false, message: "Product not found" } } when not found
    if (result.status && result.data && result.data.status !== false) {
      // Product found - return the data
      console.log('billingSlice - product found:', result.data);
      return result;
    } else {
      // Product not found
      const errorMsg = result.data?.message || result.message || 'Product not found';
      console.log('billingSlice - product not found:', errorMsg);
      return rejectWithValue(errorMsg);
    }
  } catch (error) {
    console.error('billingSlice - searchProductByCode error:', error);
    return rejectWithValue('Failed to search product');
  }
});

export const searchCustomersByPhone = createAsyncThunk<
  CustomerDTO[],
  string,
  { rejectValue: string }
>('billing/searchCustomersByPhone', async (phone, { rejectWithValue }) => {
  try {
    const result = await searchCustomersByPhoneAPI(phone);
    if (result.status) {
      return result.data!;
    } else {
      return rejectWithValue(result.message || 'No customers found');
    }
  } catch (error) {
    return rejectWithValue('Failed to search customers');
  }
});

export const createCustomer = createAsyncThunk<
  CustomerDTO,
  { name: string; phone: string; email?: string; address?: string },
  { rejectValue: string }
>('billing/createCustomer', async (customerData, { rejectWithValue }) => {
  try {
    const result = await createCustomerAPI(customerData);
    if (result.status) {
      return result.data!;
    } else {
      return rejectWithValue(result.message || 'Failed to create customer');
    }
  } catch (error: any) {
    console.error('Error creating customer:', error);
    
    // Handle different types of errors
    if (error?.response?.data?.message) {
      return rejectWithValue(error.response.data.message);
    } else if (error?.message) {
      return rejectWithValue(error.message);
    } else {
      return rejectWithValue('Failed to create customer. Please check your connection and try again.');
    }
  }
});

export const completeSale = createAsyncThunk<
  { bill: any; items: any[] },
  { 
    cartItems: CartItemDTO[];
    selectedCustomer?: CustomerDTO;
    paymentType: PaymentType;
    paidAmount: number;
    deliveryFees?: number;
    installationFees?: number;
    notes?: string;
    billDate?: string;
    storeId?: string; // Add storeId parameter
  },
  { rejectValue: string }
>('billing/completeSale', async (saleData, { rejectWithValue }) => {
  try {
    const billData = {
      storeId: saleData.storeId, // Pass storeId to backend
      customerId: saleData.selectedCustomer?.uuid,
      customerName: saleData.selectedCustomer?.name,
      customerPhone: saleData.selectedCustomer?.phone,
      items: saleData.cartItems.map(item => ({
        productId: item.productId,
        productCode: item.productCode,
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: 0,
        tax: item.tax,
      })),
      paidAmount: saleData.paidAmount,
      deliveryFees: saleData.deliveryFees || 0,
      installationFees: saleData.installationFees || 0,
      paymentType: saleData.paymentType,
      notes: saleData.notes,
      billDate: saleData.billDate,
    };

    const result = await createBillAPI(billData);
    if (result.status) {
      return result.data!;
    } else {
      return rejectWithValue(result.message || 'Failed to complete sale');
    }
  } catch (error) {
    return rejectWithValue('Failed to complete sale');
  }
});

export const fetchDailySummary = createAsyncThunk<
  DailySummaryDTO,
  string | undefined,
  { rejectValue: string }
>('billing/fetchDailySummary', async (date, { rejectWithValue }) => {
  try {
    const result = await fetchDailySummaryAPI(date);
    if (result.status) {
      return result.data!;
    } else {
      return rejectWithValue(result.message || 'Failed to fetch summary');
    }
  } catch (error) {
    return rejectWithValue('Failed to fetch daily summary');
  }
});

export const fetchCustomers = createAsyncThunk<
  { customers: any[]; total: number; meta: any },
  { search?: string; page?: number; limit?: number },
  { rejectValue: string }
>('billing/fetchCustomers', async (params, { rejectWithValue }) => {
  try {
    const res = await fetchCustomersAPI(params as any);
    console.log('fetchCustomers API response:', res);
    if (res.status) {
      // Backend returns: { status, message, data: CustomerDTO[], meta: { total, page, limit, ... } }
      const customers = Array.isArray(res.data) ? res.data : [];
      const meta = (res as any).meta || { total: customers.length, page: params.page || 1, limit: params.limit || 10 };
      const total = meta.total || customers.length;
      
      console.log('Parsed customers:', { customers, total, meta });
      
      return {
        customers: customers,
        total: total,
        meta: meta
      };
    }
    return rejectWithValue(res.message || 'Failed to fetch customers');
  } catch (error) {
    console.error('fetchCustomers error:', error);
    return rejectWithValue('Failed to fetch customers');
  }
});

// Helper function to calculate cart totals
const calculateCartTotals = (items: CartItemDTO[]): CartTotals => {
  const roundToTwo = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  let subtotal = 0;
  const totalDiscount = 0;
  let totalTax = 0;
  let totalQuantity = 0;

  items.forEach(item => {
    const itemSubtotal = roundToTwo(item.quantity * item.unitPrice);
    const taxAmount = roundToTwo((itemSubtotal * item.tax) / 100);

    subtotal = roundToTwo(subtotal + itemSubtotal);
    totalTax = roundToTwo(totalTax + taxAmount);
    totalQuantity += item.quantity;
  });

  const totalAmount = roundToTwo(subtotal - totalDiscount + totalTax);

  return {
    subtotal,
    totalDiscount,
    totalTax,
    totalAmount,
    totalQuantity,
  };
};

interface BillingState {
  // Cart state
  cartItems: CartItemDTO[];
  totals: CartTotals;
  
  // Held bills
  heldBills: {
    id: string;
    items: CartItemDTO[];
    customer?: CustomerDTO;
    totals: CartTotals;
    createdAt: string;
  }[];
  
  // Customer state
  selectedCustomer?: CustomerDTO;
  customerSearchResults: CustomerDTO[];
  customerList: { customers: any[]; total: number; meta: any } | any[];
  customerListLoading: boolean;
  customerListError: string | null;
  
  // Payment state
  paymentType: PaymentType;
  paidAmount: number;
  notes: string;
  billDate: string; // Date for the bill (defaults to today)
  
  // Product search state
  productSearchLoading: boolean;
  productSearchError: string | null;
  
  // Customer search state
  customerSearchLoading: boolean;
  customerSearchError: string | null;
  
  // Sale completion state
  saleLoading: boolean;
  saleError: string | null;
  lastCompletedSale?: { bill: any; items: any[] };
  
  // Daily summary state
  dailySummary?: DailySummaryDTO;
  summaryLoading: boolean;
  summaryError: string | null;
  
  // Session management (pure Redux)
  hasActiveSession: boolean; // Flag to track if user has active cart session
  lastSaleCompletedAt?: number; // Timestamp of last sale completion to prevent immediate draft load
}

const initialState: BillingState = {
  cartItems: [],
  totals: { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 },
  heldBills: [],
  customerSearchResults: [],
  customerList: [],
  customerListLoading: false,
  customerListError: null,
  paymentType: PaymentType.CASH,
  paidAmount: 0,
  notes: '',
  billDate: new Date().toISOString(), // Default to current date+time (full ISO timestamp)
  productSearchLoading: false,
  productSearchError: null,
  customerSearchLoading: false,
  customerSearchError: null,
  saleLoading: false,
  saleError: null,
  summaryLoading: false,
  summaryError: null,
  hasActiveSession: false,
  lastSaleCompletedAt: undefined,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    // Cart management - Pure Redux approach
    addToCart: (state, action: PayloadAction<{ product: InventoryItemDTO; quantity?: number }>) => {
      console.log('🛒 Redux addToCart action called with:', action.payload);
      console.log('🛒 Redux addToCart - Call stack:', new Error().stack);
      
      // CRITICAL DEBUG: Check if cart should be empty after recent sale
      console.log('🛒 Redux addToCart - hasActiveSession:', state.hasActiveSession);
      console.log('🛒 Redux addToCart - lastSaleCompletedAt:', state.lastSaleCompletedAt);
      if (state.lastSaleCompletedAt) {
        const timeSinceSale = Date.now() - state.lastSaleCompletedAt;
        console.log('🛒 Redux addToCart - time since last sale (seconds):', timeSinceSale / 1000);
      }
      
      const { product, quantity = 1 } = action.payload;
      console.log('🛒 Redux addToCart - product UUID:', product.uuid);
      console.log('🛒 Redux addToCart - product name:', product.item_name);  
      console.log('🛒 Redux addToCart - quantity to add:', quantity);
      console.log('🛒 Redux addToCart - current cart items before:', state.cartItems.length);
      console.log('🛒 Redux addToCart - current cart items:', state.cartItems.map(item => ({ id: item.productId, name: item.productName, qty: item.quantity })));
      
      // ALERT if cart is not empty when it should be
      if (state.cartItems.length > 0 && state.lastSaleCompletedAt && Date.now() - state.lastSaleCompletedAt < 60000) {
        console.error('🚨 CART NOT EMPTY AFTER RECENT SALE! This is the bug!');
        console.error('🚨 Cart should be empty but has items:', state.cartItems);
      }
      
      // Check if product already exists in cart
      const existingItemIndex = state.cartItems.findIndex(
        item => item.productId === product.uuid
      );
      
      if (existingItemIndex >= 0) {
        console.log('🔄 Redux addToCart - FOUND EXISTING ITEM at index:', existingItemIndex);
        console.log('🔄 Redux addToCart - existing item details:', state.cartItems[existingItemIndex]);
        console.log('🔄 Redux addToCart - existing quantity BEFORE:', state.cartItems[existingItemIndex].quantity);
        console.log('🔄 Redux addToCart - quantity being set to:', quantity);
        
        // Set quantity to the specified value (always 1 for barcode scans)
        state.cartItems[existingItemIndex].quantity = quantity;
        
        console.log('🔄 Redux addToCart - NEW quantity AFTER (SET not INCREMENT):', state.cartItems[existingItemIndex].quantity);
        console.log('🔄 Redux addToCart - Quantity reset to 1 - no more auto-increment!');
      } else {
        console.log('Redux addToCart - adding new item to cart');
        
        // Extract category name properly
        let categoryName = '';
        if (product.category) {
          if (typeof product.category === 'object' && product.category.name) {
            categoryName = product.category.name;
          } else if (typeof product.category === 'string') {
            categoryName = product.category;
          }
        } else if (product.categoryName) {
          categoryName = product.categoryName;
        }
        
        // Extract supplier name properly
        let supplierName = '';
        if (product.supplier) {
          if (typeof product.supplier === 'object' && product.supplier.name) {
            supplierName = product.supplier.name;
          } else if (typeof product.supplier === 'string') {
            supplierName = product.supplier;
          }
        } else if (product.supplierName) {
          supplierName = product.supplierName;
        }
        
        console.log('Redux addToCart - extracted categoryName:', categoryName);
        console.log('Redux addToCart - extracted supplierName:', supplierName);
        
        // Handle unit barcode products - use unit-specific price if available
        const unitPrice = product.isUnitBarcode && product.unit_selling_price 
          ? product.unit_selling_price 
          : (product.selling_price || 0);
        
        // For unit barcode products, use the unit barcode as part of productId to track separately
        const productId = product.isUnitBarcode && product.unit_barcode 
          ? `${product.uuid}__${product.unit_barcode}` 
          : product.uuid;
        
        const productCode = product.isUnitBarcode && product.unit_barcode 
          ? product.unit_barcode 
          : product.item_code;
        
        // Add new item to cart
        const cartItem: CartItemDTO = {
          productId: productId,
          productCode: productCode,
          productName: product.item_name,
          quantity,
          unitPrice: unitPrice,
          originalPrice: unitPrice, // Preserve the product master price for override detection
          discount: 0,
          tax: typeof product.tax === 'number' ? product.tax : parseFloat(product.tax) || 0,
          availableStock: product.quantity_in_stock,
          description: product.description || '',
          categoryName: categoryName,
          supplierName: supplierName,
          scale_unit: product.scale_unit || undefined,
        };
        console.log('Redux addToCart - cart item created:', cartItem);
        state.cartItems.push(cartItem);
      }
      
      console.log('Redux addToCart - cart items after addition:', state.cartItems.length);
      console.log('Redux addToCart - all cart items:', state.cartItems.map(item => ({ id: item.productId, name: item.productName })));
      
      // Recalculate totals
      state.totals = calculateCartTotals(state.cartItems);
      console.log('Redux addToCart - new totals:', state.totals);
      state.hasActiveSession = true; // Mark that we have an active cart session
    },
    
    updateCartItemQuantity: (state, action: PayloadAction<{ productId: string; quantity: number }>) => {
      const { productId, quantity } = action.payload;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex >= 0) {
        if (quantity > 0) {
          state.cartItems[itemIndex].quantity = quantity;
        } else {
          state.cartItems.splice(itemIndex, 1);
        }
        state.totals = calculateCartTotals(state.cartItems);
      }
    },
    
    updateCartItemDiscount: (state, action: PayloadAction<{ productId: string; discount: number }>) => {
      const { productId } = action.payload;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex >= 0) {
        state.cartItems[itemIndex].discount = 0;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    updateCartItemPrice: (state, action: PayloadAction<{ productId: string; unitPrice: number }>) => {
      const { productId, unitPrice } = action.payload;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex >= 0 && unitPrice >= 0) {
        const item = state.cartItems[itemIndex];
        const originalPrice = item.originalPrice;

        item.unitPrice = unitPrice;
        item.discount = 0;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    resetCartItemPrice: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex >= 0 && state.cartItems[itemIndex].originalPrice !== undefined) {
        state.cartItems[itemIndex].unitPrice = state.cartItems[itemIndex].originalPrice!;
        state.cartItems[itemIndex].discount = 0;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },
    
    removeFromCart: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      console.log('Redux removeFromCart - productId:', productId);
      console.log('Redux removeFromCart - current items:', state.cartItems.length);
      console.log('Redux removeFromCart - current item IDs:', state.cartItems.map(item => item.productId));
      
      const initialLength = state.cartItems.length;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex !== -1) {
        // Remove using splice to ensure Immer detects the change properly
        state.cartItems.splice(itemIndex, 1);
        console.log('Redux removeFromCart - item found and removed at index:', itemIndex);
      } else {
        console.log('Redux removeFromCart - item not found with productId:', productId);
      }
      
      console.log('Redux removeFromCart - after removal:', state.cartItems.length);
      console.log('Redux removeFromCart - removed?', initialLength !== state.cartItems.length);
      
      // Recalculate totals
      state.totals = calculateCartTotals(state.cartItems);
      
      // REMOVED: localStorage update - Pure Redux approach eliminates localStorage needs
    },
    
    clearCart: (state) => {
      console.log('Redux clearCart action called - current items:', state.cartItems.length);
      console.log('Redux clearCart - before clear, cartItems reference:', state.cartItems);
      
      // Clear cart items using splice to ensure Immer detects the change
      state.cartItems.splice(0, state.cartItems.length);
      
      // Reset totals to match initial state exactly
      state.totals.subtotal = 0;
      state.totals.totalDiscount = 0;
      state.totals.totalTax = 0;
      state.totals.totalAmount = 0;
      
      // Reset other related state
      state.selectedCustomer = undefined;
      state.paymentType = PaymentType.CASH;
      state.paidAmount = 0;
      state.notes = '';
      
      // REMOVED: localStorage clearing - Pure Redux approach eliminates localStorage needs
      
      console.log('Redux clearCart action completed - items now:', state.cartItems.length);
      console.log('Redux clearCart - after clear, cartItems reference:', state.cartItems);
      console.log('Redux clearCart - totals reset:', state.totals);
      
      // Reset active session flag
      state.hasActiveSession = false;
      console.log('Redux clearCart - hasActiveSession reset to false');
    },

    // Hold current bill for later
    holdBill: (state) => {
      if (state.cartItems.length > 0) {
        const holdBill = {
          id: Date.now().toString(),
          items: [...state.cartItems],
          customer: state.selectedCustomer,
          totals: { ...state.totals },
          createdAt: new Date().toISOString(),
        };
        
        state.heldBills.push(holdBill);
        
        // Clear current cart
        state.cartItems = [];
        state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
        state.selectedCustomer = undefined;
      }
    },

    // Restore held bill
    restoreHeldBill: (state, action: PayloadAction<string>) => {
      const billId = action.payload;
      const heldBillIndex = state.heldBills.findIndex(bill => bill.id === billId);
      
      if (heldBillIndex >= 0) {
        const heldBill = state.heldBills[heldBillIndex];
        
        // Restore the held bill to current cart
        state.cartItems = [...heldBill.items];
        state.totals = { ...heldBill.totals };
        state.selectedCustomer = heldBill.customer;
        
        // Remove from held bills
        state.heldBills.splice(heldBillIndex, 1);
      }
    },

    // Delete held bill
    deleteHeldBill: (state, action: PayloadAction<string>) => {
      const billId = action.payload;
      state.heldBills = state.heldBills.filter(bill => bill.id !== billId);
    },
    
    // Customer management
    setSelectedCustomer: (state, action: PayloadAction<CustomerDTO | undefined>) => {
      state.selectedCustomer = action.payload;
    },

    loadQuotationIntoCart: (state, action: PayloadAction<{ items: CartItemDTO[]; customer?: CustomerDTO; notes?: string }>) => {
      state.cartItems = action.payload.items.map((item) => ({ ...item }));
      state.selectedCustomer = action.payload.customer;
      state.notes = action.payload.notes || '';
      state.totals = calculateCartTotals(state.cartItems);
      state.hasActiveSession = state.cartItems.length > 0;
      state.lastSaleCompletedAt = undefined;
    },
    
    clearCustomerSearch: (state) => {
      state.customerSearchResults = [];
      state.customerSearchError = null;
    },
    
    // Payment management
    setPaymentType: (state, action: PayloadAction<PaymentType>) => {
      state.paymentType = action.payload;
    },
    
    setPaidAmount: (state, action: PayloadAction<number>) => {
      state.paidAmount = Math.max(0, action.payload);
    },
    
    setNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
    },
    
    setBillDate: (state, action: PayloadAction<string>) => {
      state.billDate = action.payload;
    },
    
    // REMOVED: saveDraft - Pure Redux approach, no localStorage needed
    
    // REMOVED: loadDraft - Pure Redux approach eliminates localStorage draft system
    
    // REMOVED: clearDraft - Pure Redux approach, no localStorage needed
    
    // Manual action to clear sale completion flag when user intentionally starts new session
    clearSaleCompletionFlag: (state) => {
      console.log('🔓 Redux clearSaleCompletionFlag - clearing recent sale flag');
      state.lastSaleCompletedAt = undefined;
      // REMOVED: localStorage clearing - Pure Redux approach
    },
    
    // Reset state after successful sale
    resetAfterSale: (state) => {
      console.log('🔄 Redux resetAfterSale - clearing all state');
      console.log('🔄 Redux resetAfterSale - BEFORE clear - cartItems:', state.cartItems.length);
      console.log('🔄 Redux resetAfterSale - BEFORE clear - cartItems details:', state.cartItems.map(item => ({ id: item.productId, name: item.productName, qty: item.quantity })));
      
      state.cartItems = [];
      state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
      state.selectedCustomer = undefined;
      state.paymentType = PaymentType.CASH;
      state.paidAmount = 0;
      state.notes = '';
      state.billDate = new Date().toISOString(); // Reset to current timestamp for next sale
      state.saleError = null;
      state.customerSearchResults = [];
      state.lastCompletedSale = undefined; // Clear stale sale data
      
      // REMOVED: localStorage clearing - Pure Redux approach eliminates need for localStorage
      
      state.hasActiveSession = false; // Reset active session flag
      state.lastSaleCompletedAt = Date.now(); // Mark when sale was completed
      
      // REMOVED: localStorage flag setting - Pure Redux state tracking is sufficient
      
      console.log('🔄 Redux resetAfterSale - AFTER clear - cartItems:', state.cartItems.length);
      console.log('🔄 Redux resetAfterSale - AFTER clear - hasActiveSession:', state.hasActiveSession);
      console.log('🔄 Redux resetAfterSale - state reset complete, sale completed at:', state.lastSaleCompletedAt);
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.productSearchError = null;
      state.customerSearchError = null;
      state.saleError = null;
      state.summaryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Product search
      .addCase(searchProductByCode.pending, (state) => {
        state.productSearchLoading = true;
        state.productSearchError = null;
      })
      .addCase(searchProductByCode.fulfilled, (state, action) => {
        state.productSearchLoading = false;
        // Product found, we'll handle adding to cart in the component
      })
      .addCase(searchProductByCode.rejected, (state, action) => {
        state.productSearchLoading = false;
        state.productSearchError = (typeof action.payload === 'string' ? action.payload : 'Failed to search product');
      })
      
      // Customer search
      .addCase(searchCustomersByPhone.pending, (state) => {
        state.customerSearchLoading = true;
        state.customerSearchError = null;
      })
      .addCase(searchCustomersByPhone.fulfilled, (state, action) => {
        state.customerSearchLoading = false;
        state.customerSearchResults = action.payload;
      })
      .addCase(searchCustomersByPhone.rejected, (state, action) => {
        state.customerSearchLoading = false;
        state.customerSearchError = action.payload || 'Failed to search customers';
        state.customerSearchResults = [];
      })
      .addCase(fetchCustomers.pending, (state) => {
        state.customerListLoading = true;
        state.customerListError = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customerListLoading = false;
        state.customerList = action.payload; // Store the entire payload with customers, total, meta
        state.customerListError = null;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.customerListLoading = false;
        state.customerListError = (action.payload as string) || 'Failed to fetch customers';
        state.customerList = [];
      })
      
      // Customer creation
      .addCase(createCustomer.pending, (state) => {
        state.customerSearchError = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.selectedCustomer = action.payload;
        state.customerSearchError = null;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.customerSearchError = action.payload || 'Failed to create customer';
      })
      
      // Sale completion
      .addCase(completeSale.pending, (state) => {
        state.saleLoading = true;
        state.saleError = null;
      })
      .addCase(completeSale.fulfilled, (state, action) => {
        state.saleLoading = false;
        state.lastCompletedSale = action.payload;
        // Don't reset state here - let component handle it after showing success
      })
      .addCase(completeSale.rejected, (state, action) => {
        state.saleLoading = false;
        state.saleError = action.payload || 'Failed to complete sale';
      })
      
      // Daily summary
      .addCase(fetchDailySummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchDailySummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.dailySummary = action.payload;
      })
      .addCase(fetchDailySummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError = action.payload || 'Failed to fetch daily summary';
      });
  },
});

export const {
  addToCart,
  updateCartItemQuantity,
  updateCartItemDiscount,
  updateCartItemPrice,
  resetCartItemPrice,
  removeFromCart,
  clearCart,
  holdBill,
  restoreHeldBill,
  deleteHeldBill,
  setSelectedCustomer,
  loadQuotationIntoCart,
  clearCustomerSearch,
  setPaymentType,
  setPaidAmount,
  setNotes,
  setBillDate,
  clearSaleCompletionFlag,
  resetAfterSale,
  clearErrors,
} = billingSlice.actions;

export default billingSlice.reducer;
